/**
 * The before/after overview of an opened rewrite (DESIGN UD-22): titled with the document's name and the job's date, it shows
 * the machine-authorship index before and after (the calibrated index the overview headline shows, its flagged or not-flagged
 * state and the change), the translation index when the document has one, and a table of EVERY channel with its document
 * score before, after and the change. The channels that rose above their baseline are marked; a channel that does not apply is
 * shown as such; the table sorts by the largest change by default. The "after" figures are a full analysis of the rewritten
 * text (`overview-control.ts`), made once and kept with the job; while it runs the dialog shows progress with a time estimate
 * (UD-11). The lens (passages, reverts, failure reports) stays where it was, behind this dialog. No figure here is called a
 * probability; the statuses are the engine's own words. Text nodes only.
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { LX04_INDEX_NOTE } from '../../engine/bench/lx04-note.ts';
import { caveat } from '../../engine/ensemble/wording.ts';
import {
	type HumanComparison,
	compareHuman,
	compareToHuman,
} from '../../shared/human-reference.ts';
import {
	type AnalysisSummary,
	type ChannelCompare,
	type ChannelSort,
	type IndexSummary,
	compareChannels,
	indexChange,
	sortCompare,
} from '../../shared/summary.ts';
import { DataTable, type SortState } from '../components/DataTable.tsx';
import { ChannelTerm } from '../components/Term.tsx';
import { createEstimator } from '../estimate.ts';
import { fmt1, fmtInt, fmtSigned1 } from '../format.ts';
import { navigate } from '../router.ts';
import { HUMAN_REF as HR, OVERVIEW as O, VERSIONS } from '../strings-bench.ts';
import { Dialog, Notice } from '../ui.tsx';
import {
	HumanCell,
	HumanCountLine,
	HumanLegend,
	HumanRange,
	HumanSelector,
	humanStrata,
	setHumanStrata,
	useHumanCounts,
	useHumanReference,
} from './HumanCompare.tsx';
import { overviewLead } from './action-label.ts';
import { baseOf } from './chain.ts';
import { fmtJobTime, jobSetup, jobTitle } from './jobs-format.ts';
import { cancelMeasure, closeOverview, retryOverview } from './overview-control.ts';
import {
	chain,
	lensOpen,
	overview,
	overviewError,
	overviewOpen,
	overviewProgress,
} from './state.ts';

/** One index figure, its state and what it means, for one side of the comparison. */
function Side({
	label,
	figure,
	testId,
	pending,
}: { label: string; figure: IndexSummary | null; testId: string; pending: boolean }) {
	return (
		<div class="ov-side">
			<div class="eyebrow">{label}</div>
			<div class="ov-fig" data-testid={testId}>
				{figure === null ? (pending ? '…' : '–') : figure.value === null ? '–' : fmt1(figure.value)}
			</div>
			{figure === null ? null : figure.kind === 'calibrated' ? (
				<>
					<span
						class="ov-flag"
						data-flag={figure.flagged ? 'yes' : 'no'}
						data-testid={`${testId}-flag`}
					>
						{figure.flagged ? O.flagged : O.notFlagged}
					</span>
					<div class="dim ov-phrase">{figure.statusPhrase}</div>
				</>
			) : figure.kind === 'uncalibrated' ? (
				<span class="ov-flag" data-flag="none">
					{O.uncalibrated}
				</span>
			) : (
				<div class="dim ov-phrase">{O.noValue}</div>
			)}
		</div>
	);
}

function IndexCard({
	title,
	testId,
	before,
	after,
	pending,
}: {
	title: string;
	testId: string;
	before: IndexSummary | null;
	after: IndexSummary | null;
	pending: boolean;
}) {
	const change = before && after ? indexChange(before, after) : null;
	const ref = before?.referenceIndex ?? after?.referenceIndex ?? null;
	return (
		<section class="ov-card" data-testid={testId} aria-label={title}>
			<h3 class="ov-card-title">{title}</h3>
			<div class="ov-pair">
				<Side label={O.before} figure={before} testId={`${testId}-before`} pending={false} />
				<div class="ov-arrow" aria-hidden="true">
					→
				</div>
				<Side label={O.after} figure={after} testId={`${testId}-after`} pending={pending} />
				<div class="ov-change">
					<div class="eyebrow">{O.change}</div>
					<div class="ov-fig ov-fig-change" data-testid={`${testId}-change`}>
						{change === null ? '–' : fmtSigned1(change)}
					</div>
				</div>
			</div>
			{ref !== null ? <p class="dim ov-ref">{O.reference(fmt1(ref))}</p> : null}
		</section>
	);
}

/** The progress line of the measurements (the original working text, then the rewritten text), with the time estimate once one can be made. */
function Measuring() {
	const p = overviewProgress.value;
	const [now, setNow] = useState(performance.now());
	const [est] = useState(() =>
		p ? createEstimator({ startedAt: p.startedAt, priorMs: p.priorMs }) : null
	);
	useEffect(() => {
		const t = window.setInterval(() => setNow(performance.now()), 1000);
		return () => window.clearInterval(t);
	}, []);
	if (!p) return null;
	const left = est?.update(now, p.fraction)?.text ?? null;
	const label = p.side === 'before' ? O.measuringBefore : O.measuring;
	return (
		<output class="ov-measuring" data-testid="ov-measuring">
			<p>
				<strong>{label}</strong>
				{left ? <span class="dim"> · {left}</span> : null}
			</p>
			<progress class="ov-bar" value={Math.round(p.fraction * 100)} max={100} aria-label={label} />
			<p class="dim">{O.measuringHelp}</p>
		</output>
	);
}

/** A channel's row: the before/after comparison and, once the reference is loaded, both sides against human writing. */
type Row = ChannelCompare & { hb: HumanComparison | null; ha: HumanComparison | null };

/**
 * The rows in the chosen order. The two human columns sort by the distance from the human range, the farthest first
 * (`compareHuman`); ascending reverses the measured cells and leaves unmeasured and not-applicable ones last.
 */
function sortRows(rows: readonly Row[], sort: SortState): Row[] {
	const id = sort?.id ?? 'change';
	if (id !== 'hb' && id !== 'ha') {
		const order = sortCompare(rows, id as ChannelSort, sort?.dir ?? 'desc').map((r) => r.id);
		const byId = new Map(rows.map((r) => [r.id, r]));
		return order.map((k) => byId.get(k) as Row);
	}
	const side = (r: Row) => r[id];
	const measured = (r: Row) => side(r)?.status === 'measured';
	const sorted = [...rows].sort(
		(a, b) =>
			compareHuman(
				side(a) ?? { status: 'not-applicable' },
				side(b) ?? { status: 'not-applicable' }
			) || (a.id < b.id ? -1 : 1)
	);
	if (sort?.dir !== 'asc') return sorted;
	return [...sorted.filter(measured).reverse(), ...sorted.filter((r) => !measured(r))];
}

function ChannelTable({
	rows,
	sort,
	onSort,
}: { rows: Row[]; sort: SortState; onSort: (id: string) => void }) {
	const shown = sortRows(rows, sort);
	const score = (v: number | null, r: ChannelCompare) =>
		v === null ? (
			<span class="dim" title={r.reason}>
				{O.notApplicable}
			</span>
		) : (
			fmt1(v)
		);
	return (
		<DataTable
			testId="ov-channels"
			caption={O.channelsTitle}
			rows={shown}
			rowKey={(r) => r.id}
			sort={sort}
			onSort={onSort}
			rowClass={(r) => (r.over ? 'ov-over' : undefined)}
			columns={[
				{
					id: 'id',
					header: O.cols.id,
					sortable: true,
					rowHeader: true,
					className: 'ov-chan',
					render: (r) => (
						<span data-channel={r.id}>
							<ChannelTerm id={r.id} name={r.name} />
						</span>
					),
				},
				{
					id: 'before',
					header: O.cols.before,
					sortable: true,
					num: true,
					render: (r) => score(r.before, r),
				},
				{
					id: 'hb',
					header: HR.cols.before,
					sortable: true,
					className: 'hr-col',
					render: (r) => <HumanCell c={r.hb} testId={`hr-before-${r.id}`} />,
				},
				{
					id: 'after',
					header: O.cols.after,
					sortable: true,
					num: true,
					render: (r) => score(r.after, r),
				},
				{
					id: 'ha',
					header: HR.cols.after,
					sortable: true,
					className: 'hr-col',
					render: (r) => <HumanCell c={r.ha} testId={`hr-after-${r.id}`} />,
				},
				{
					id: 'change',
					header: O.cols.change,
					sortable: true,
					num: true,
					render: (r) => (r.change === null ? '–' : fmtSigned1(r.change)),
				},
				{
					id: 'range',
					header: HR.cols.range,
					className: 'hr-col',
					render: (r) => <HumanRange c={r.ha} />,
				},
				{
					id: 'mark',
					header: O.cols.mark,
					render: (r) => (r.over ? <span class="ov-mark">{O.over}</span> : ''),
				},
			]}
		/>
	);
}

function Body({
	before,
	after,
}: { before: AnalysisSummary | null; after: AnalysisSummary | null }) {
	const data = overview.value;
	const [sort, setSort] = useState<SortState>({ id: 'change', dir: 'desc' });
	const onSort = (id: string) =>
		setSort(
			sort?.id === id
				? { id, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
				: { id, dir: id === 'id' ? 'asc' : 'desc' }
		);
	const human = useHumanReference();
	const sel = human.ref ? humanStrata.value : null;
	const rows = useMemo<Row[]>(() => {
		if (!data || !before || !after) return [];
		const ref = human.ref;
		return compareChannels(before, after, data.over).map((r) => ({
			...r,
			hb: ref && sel ? compareToHuman(ref, r.id, r.before, before.words, sel) : null,
			ha: ref && sel ? compareToHuman(ref, r.id, r.after, after.words, sel) : null,
		}));
	}, [data, before, after, human.ref, sel]);
	// how many channels are red and outside on each side, against human documents of that length (UD-26)
	const countsBefore = useHumanCounts(
		human.ref,
		rows.map((r) => r.hb),
		before?.words ?? null,
		sel
	);
	const countsAfter = useHumanCounts(
		human.ref,
		rows.map((r) => r.ha),
		after?.words ?? null,
		sel
	);
	if (!data) return null;
	const measuring = overviewProgress.value !== null;
	const err = overviewError.value;
	const hasTranslation =
		(before?.translation.kind ?? 'none') !== 'none' ||
		(after?.translation.kind ?? 'none') !== 'none';
	// LX-04 feeds the index only under 1,000 prose words (ED-170): the note appears when either side is that short
	const shortText =
		Math.min(before?.words ?? Number.POSITIVE_INFINITY, after?.words ?? Number.POSITIVE_INFINITY) <
		1000;
	return (
		<div class="ov" data-testid="rewrite-overview">
			<p class="dim ov-sub" data-testid="ov-sub">
				{jobSetup(data.job)}
				{before && after && before.words !== after.words
					? ` · ${O.words(fmtInt(before.words), fmtInt(after.words))}`
					: ''}
			</p>
			<div class="ov-indexes">
				<IndexCard
					title={O.authorship}
					testId="ov-authorship"
					before={before?.authorship ?? null}
					after={after?.authorship ?? null}
					pending={measuring}
				/>
				{hasTranslation ? (
					<IndexCard
						title={O.translation}
						testId="ov-translation"
						before={before?.translation ?? null}
						after={after?.translation ?? null}
						pending={measuring}
					/>
				) : null}
			</div>
			<p class="dim ov-note">{O.scopeNote}</p>
			<p class="dim ov-note" data-testid="overview-started-from">
				{VERSIONS.startedFrom(String(baseOf(data.job).n))}
			</p>
			{before?.authorship.kind === 'uncalibrated' ? (
				<p class="dim ov-note">{O.uncalibratedNote}</p>
			) : null}
			{shortText && before?.authorship.kind === 'calibrated' ? (
				<p class="dim ov-note" data-testid="ov-lx04-note">
					{LX04_INDEX_NOTE}
				</p>
			) : null}
			<p class="dim ov-note" data-testid="ov-caveat">
				{caveat(before?.calibrated ?? true, before?.authorship.kind === 'calibrated')}
			</p>
			{measuring ? <Measuring key={overviewProgress.value?.startedAt} /> : null}
			{err ? (
				<Notice kind="error">
					{err}{' '}
					<button type="button" class="btn-text" onClick={retryOverview}>
						{O.retry}
					</button>
				</Notice>
			) : null}
			{!measuring && !err && !after ? (
				<p class="dim">
					{O.noAfter}{' '}
					<button type="button" class="btn-text" onClick={retryOverview}>
						{O.retry}
					</button>
				</p>
			) : null}
			{rows.length > 0 ? (
				<>
					<h3>{O.channelsTitle}</h3>
					<p class="dim">{O.channelsHelp}</p>
					<section class="hr" aria-label={HR.title} data-testid="hr">
						<p class="dim">{HR.help}</p>
						{human.ref && sel ? (
							<>
								<HumanSelector reference={human.ref} selected={sel} onChange={setHumanStrata} />
								{sel.size === 0 ? (
									<Notice kind="info">{HR.noneSelected}</Notice>
								) : (
									<>
										<HumanLegend />
										<HumanCountLine
											side={HR.counts.before}
											summary={countsBefore}
											testId="hr-counts-before"
										/>
										<HumanCountLine
											side={HR.counts.after}
											summary={countsAfter}
											testId="hr-counts-after"
										/>
									</>
								)}
							</>
						) : (
							<p class="dim" data-testid="hr-status">
								{human.error ? HR.failed : HR.loading}
							</p>
						)}
					</section>
					<div class="ov-channels">
						<ChannelTable rows={rows} sort={sort} onSort={onSort} />
					</div>
					<p class="dim ov-note" data-testid="hr-caveat">
						{HR.caveat}
						{human.ref && sel
							? ` ${HR.source(fmtInt(human.ref.strata.reduce((n, s) => n + (sel.has(s.id) ? s.n : 0), 0)))}`
							: ''}
					</p>
				</>
			) : null}
		</div>
	);
}

export function RewriteOverviewDialog() {
	const data = overview.value;
	const open = overviewOpen.value;
	const measuring = overviewProgress.value !== null;
	const title = data
		? O.title(
				jobTitle(data.job),
				fmtJobTime(data.job.createdAt),
				overviewLead(chain.value, data.job)
			)
		: O.reopen;
	return (
		<Dialog
			title={title}
			open={open}
			onClose={closeOverview}
			actions={
				<>
					{measuring ? (
						<button
							type="button"
							class="btn btn-secondary"
							data-testid="ov-cancel"
							onClick={cancelMeasure}
						>
							{O.cancelMeasure}
						</button>
					) : null}
					<button
						type="button"
						class="btn btn-secondary"
						data-testid="ov-show-passages"
						onClick={() => {
							lensOpen.value = true;
							navigate('#/read');
							closeOverview();
						}}
					>
						{O.showPassages}
					</button>
					<button
						type="button"
						class="btn btn-primary"
						data-testid="ov-close"
						onClick={closeOverview}
					>
						{O.close}
					</button>
				</>
			}
		>
			{open && data ? <Body before={data.before} after={data.after} /> : null}
		</Dialog>
	);
}
